import { instanceBo } from "../utils/axios.create";

export interface EnrollmentSummaryFilters {
	accountId: string;
	programId: string;
	orgUnitId?: string;
	researcherId?: string;
	startDate?: string;
	endDate?: string;
	pageNumber?: number;
	pageSize?: number;
}

export interface EnrollmentSummaryKpis {
	totalEnrolled: number;
	active: number;
	completed: number;
	disenrolled: number;
	avgCompletionDays: number;
}

export interface EnrollmentSummaryByOrgUnit {
	orgUnitId: string;
	orgUnit: string;
	active: number;
	completed: number;
	disenrolled: number;
	paused: number;
}

export interface EnrollmentSummaryRow {
	enrollmentId: string;
	participantName: string;
	orgUnit: string;
	researcher: string;
	startDate: string | null;
	day: number;
	totalDays: number;
	status: "ACTIVE" | "COMPLETED" | "DISENROLLED" | "PAUSED";
	completionPct: number;
}

export interface EnrollmentSummaryFilterOption {
	id: string;
	name: string;
}

export interface EnrollmentSummaryPagination {
	pageNumber: number;
	pageSize: number;
	total: number;
}

export interface EnrollmentSummaryResponse {
	kpis: EnrollmentSummaryKpis;
	byOrgUnit: EnrollmentSummaryByOrgUnit[];
	enrollments: EnrollmentSummaryRow[];
	availableOrgUnits: EnrollmentSummaryFilterOption[];
	availableResearchers: EnrollmentSummaryFilterOption[];
	pagination: EnrollmentSummaryPagination;
}

const getEnrollmentSummary = async (
	filters: EnrollmentSummaryFilters,
): Promise<EnrollmentSummaryResponse> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.orgUnitId) params.orgUnitId = filters.orgUnitId;
	if (filters.researcherId) params.researcherId = filters.researcherId;
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;
	if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
	if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;

	return instanceBo
		.get("/reports/enrollment-summary", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

export interface IncentiveDisbursementFilters {
	accountId: string;
	programId: string;
	orgUnitId?: string;
	researcherId?: string;
	startDate?: string;
	endDate?: string;
	pageNumber?: number;
	pageSize?: number;
}

export interface IncentiveDisbursementKpis {
	totalEarned: number;
	totalReceived: number;
	avgEarned: number;
	potential: number;
	engagementRate: number;
}

export interface IncentiveDisbursementByDay {
	day: number;
	earned: number;
	potential: number;
}

export interface IncentiveDisbursementByEnrollment {
	enrollmentId: string;
	participantName: string;
	orgUnit: string;
	researcher: string;
	potential: number;
	earned: number;
	received: number;
	engagementRate: number;
}

export interface IncentiveDisbursementResponse {
	kpis: IncentiveDisbursementKpis;
	byDay: IncentiveDisbursementByDay[];
	byEnrollment: IncentiveDisbursementByEnrollment[];
	availableOrgUnits: EnrollmentSummaryFilterOption[];
	availableResearchers: EnrollmentSummaryFilterOption[];
	pagination: EnrollmentSummaryPagination;
}

const getIncentiveDisbursement = async (
	filters: IncentiveDisbursementFilters,
): Promise<IncentiveDisbursementResponse> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.orgUnitId) params.orgUnitId = filters.orgUnitId;
	if (filters.researcherId) params.researcherId = filters.researcherId;
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;
	if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
	if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;

	return instanceBo
		.get("/reports/incentive-disbursement", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

export interface EducationalEngagementFilters {
	accountId: string;
	programId: string;
	startDate?: string;
	endDate?: string;
	pageNumber?: number;
	pageSize?: number;
}

export interface EducationalEngagementKpis {
	contentItems: number;
	totalAccesses: number;
	avgAccessRate: number;
}

export interface EducationalEngagementByItem {
	itemId: string;
	title: string;
	type: string;
	accesses: number;
	accessRate: number;
}

export interface EducationalEngagementItemFlag {
	itemId: string;
	accessed: boolean;
}

export interface EducationalEngagementByEnrollment {
	enrollmentId: string;
	participantName: string;
	items: EducationalEngagementItemFlag[];
}

export interface EducationalEngagementResponse {
	kpis: EducationalEngagementKpis;
	byItem: EducationalEngagementByItem[];
	byEnrollment: EducationalEngagementByEnrollment[];
	pagination: EnrollmentSummaryPagination;
}

const getEducationalEngagement = async (
	filters: EducationalEngagementFilters,
): Promise<EducationalEngagementResponse> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;
	if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
	if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;

	return instanceBo
		.get("/reports/educational-engagement", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

export interface EventActivityFilters {
	accountId: string;
	programId: string;
	startDate?: string;
	endDate?: string;
	pageNumber?: number;
	pageSize?: number;
}

export interface EventActivityEvent {
	eventId: string;
	name: string;
}

export interface EventActivityKpis {
	expected: number;
	submitted: number;
	missed: number;
	pending: number;
}

export type EventActivityStatus = "SUBMITTED" | "MISSED" | "PENDING";

export interface EventActivitySubmission {
	eventId: string;
	status: EventActivityStatus;
}

export interface EventActivityDay {
	day: number;
	submissions: EventActivitySubmission[];
}

export interface EventActivityRow {
	enrollmentId: string;
	participantName: string;
	orgUnit: string;
	completionPct: number;
	days: EventActivityDay[];
}

export interface EventActivityResponse {
	events: EventActivityEvent[];
	kpis: EventActivityKpis;
	rows: EventActivityRow[];
	pagination: EnrollmentSummaryPagination;
}

const getEventActivity = async (
	filters: EventActivityFilters,
): Promise<EventActivityResponse> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;
	if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
	if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;

	return instanceBo
		.get("/reports/event-activity", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

export interface EventRatingsFilters {
	accountId: string;
	programId: string;
	eventId?: string;
	enrollmentId?: string;
	startDate?: string;
	endDate?: string;
}

export type EventRatingType = "SCORED_SUM" | "INDEPENDENT_RATINGS";

export interface EventRatingsEvent {
	eventId: string;
	name: string;
	ratingType: EventRatingType;
}

export interface EventRatingsEnrollment {
	enrollmentId: string;
	participantName: string;
}

export interface ScoredSumDay {
	day: number;
	avgScore: number | null;
	submissionCount: number;
}

export interface ScoredSumKpis {
	avgScoreDay1: number | null;
	avgScoreDay14: number | null;
	improvementPct: number | null;
	submissionRate: number;
}

export interface ScoredSumPayload {
	scoreMin: number;
	scoreMax: number;
	avgScoreByDay: ScoredSumDay[];
	kpis: ScoredSumKpis;
}

export interface IndependentDistressDay {
	day: number;
	avgDistress: number | null;
	submissionCount: number;
}

export interface IndependentEmojiDay {
	day: number;
	counts: Record<string, number>;
}

export interface IndependentEmojiPick {
	value: string;
	count: number;
}

export interface IndependentKpis {
	avgDistressDay1: number | null;
	avgDistressDay14: number | null;
	mostCommonEmojiDay1: IndependentEmojiPick | null;
	submissionRate: number;
}

export interface IndependentRatingsPayload {
	distressField: { name: string; min: number; max: number };
	emojiField: { name: string; values: string[] };
	avgDistressByDay: IndependentDistressDay[];
	emojiCountsByDay: IndependentEmojiDay[];
	kpis: IndependentKpis;
}

export interface EventRatingsChart {
	eventId: string;
	name: string;
	ratingType: EventRatingType;
	scoredSum?: ScoredSumPayload;
	independent?: IndependentRatingsPayload;
}

export interface EventRatingsResponse {
	events: EventRatingsEvent[];
	enrollments: EventRatingsEnrollment[];
	chart: EventRatingsChart | null;
}

const getEventRatings = async (
	filters: EventRatingsFilters,
): Promise<EventRatingsResponse> => {
	const params: Record<string, string> = {
		programId: filters.programId,
	};
	if (filters.eventId) params.eventId = filters.eventId;
	if (filters.enrollmentId) params.enrollmentId = filters.enrollmentId;
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;

	return instanceBo
		.get("/reports/event-ratings", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

export const reportsService = {
	getEnrollmentSummary,
	getIncentiveDisbursement,
	getEducationalEngagement,
	getEventActivity,
	getEventRatings,
};
